import { useRef } from "react";
import { Button, Empty, Spin, Typography } from "antd";
import { useFormatTemplateStore } from "@/state/formatTemplateStore";
import { bufferToBase64Image } from "@/utils/formatTemplate/slides-extraction-handlers";
import useFormatTemplates from "@/utils/formatTemplate/useFormatTemplates";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import useFormatTemplatesRecentlyUsed from "@/utils/formatTemplate/useRecentFormatTemplates";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/scrollbar";
import StarIcon from "@/formatTemplate/components/StarIcon";

const { Text, Title } = Typography;

export function FormatTemplateSearch() {
  const { setFormatTemplateSidebar } = useFormatTemplateStore();
  const listRef = useRef<HTMLDivElement>(null);
  const prevButtonRef = useRef<HTMLButtonElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const swiperRef = useRef<any>(null);

  const {
    formatTemplates,
    searchQuery,
    isLoading,
    hasMore,
    loadMore,
    handleSearchChange,
    fetchTemplates,
    handlePrimaryTemplate,
  } = useFormatTemplates(10);
  const {
    formatTemplatesRecentlyUsed,
    isLoading: isLoadingRecentlyUsed,
    fetchTemplates: fetchTemplatesRecentlyUsed,
  } = useFormatTemplatesRecentlyUsed(10);

  const limitedRecentlyUsedTemplates = formatTemplatesRecentlyUsed?.slice(0, 10) || [];

  const handleScroll = () => {
    if (!listRef.current || isLoading || !hasMore) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10;

    if (isNearBottom) {
      loadMore();
    }
  };

  const getImageSrc = (img: any) => bufferToBase64Image(img?.buffer?.data);

  return (
    <div className={"flex flex-col h-full min-h-screen grid-rows-[auto_1fr_auto] gap-x-2 px-4 py-4"} style={{ overflow: 'visible' }}>
      <Text>
        Any content created on the AlphaScript platform is designed in the Format Template of your choosing. Below you
        can select your primary template, review the current list of available templates within your Organization and
        Create New Format Templates.
      </Text>
      <div className={"w-1/2 flex gap-10 my-6 items-center"}>
        <Text>Create New Template:</Text>
        <Button type={"primary"} onClick={() => setFormatTemplateSidebar("create-new")}>
          Create New
        </Button>
      </div>

      <div className="flex flex-col mt-0" style={{ overflow: 'visible' }}>
        <Title level={3} className={"font-bold"}>
          Recently Used
        </Title>
        {isLoadingRecentlyUsed ? (
          <div className="w-full py-6 flex items-center justify-center">
            <Spin size="large" />
          </div>
        ) : limitedRecentlyUsedTemplates && limitedRecentlyUsedTemplates.length > 0 ? (
          <div className="w-full max-w-[95%] mx-auto relative" style={{ overflow: 'visible', paddingLeft: '20px', paddingRight: '20px' }}>
            {/* Custom Navigation Buttons */}
            {limitedRecentlyUsedTemplates.length > 4 && (
              <>
                <button
                  ref={prevButtonRef}
                  className="absolute -left-3 z-50 flex items-center justify-center transition-all duration-300 hover:scale-110 disabled:opacity-35 disabled:cursor-not-allowed bg-transparent border-none p-0 cursor-pointer"
                  aria-label="Previous slide"
                  style={{ 
                    pointerEvents: 'auto',
                    top: 'calc((180px * 111 / 205) / 2 - 50px)',
                  }}
                >
                  <svg width="25" height="120" viewBox="0 0 50 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <polygon points="40,5 40,95 10,50" fill="#D3D3D3" stroke="#333" strokeWidth="1"/>
                  </svg>
                </button>
                <button
                  ref={nextButtonRef}
                  className="absolute -right-3 z-50 flex items-center justify-center transition-all duration-300 hover:scale-110 disabled:opacity-35 disabled:cursor-not-allowed bg-transparent border-none p-0 cursor-pointer"
                  aria-label="Next slide"
                  style={{ 
                    pointerEvents: 'auto',
                    top: 'calc((180px * 111 / 205) / 2 - 50px)',
                  }}
                >
                  <svg width="25" height="120" viewBox="0 0 50 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <polygon points="10,5 10,95 40,50" fill="#D3D3D3" stroke="#333" strokeWidth="1"/>
                  </svg>
                </button>
              </>
            )}
            <Swiper
              modules={[Navigation]}
              spaceBetween={0}
              slidesPerView={4}
              navigation={
                limitedRecentlyUsedTemplates.length > 4
                  ? {
                      prevEl: prevButtonRef.current,
                      nextEl: nextButtonRef.current,
                    }
                  : false
              }
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
              }}
              onBeforeInit={(swiper) => {
                if (swiper.params.navigation && typeof swiper.params.navigation !== 'boolean') {
                  const navigation = swiper.params.navigation as any;
                  navigation.prevEl = prevButtonRef.current;
                  navigation.nextEl = nextButtonRef.current;
                }
              }}
              className={"w-full overflow-visible"}
            >
              {limitedRecentlyUsedTemplates?.map((format, index) => (
                <SwiperSlide key={`recent-${index}`}>
                  <div className="flex flex-col items-center w-full mx-auto max-w-[180px]">
                  <div
                    className={`relative aspect-[205/111]  w-full h-26  border-2 border-black flex items-center justify-center  text-white`}
                  >
                    <button
                      type="button"
                      className={`absolute   top-1 right-1 flex items-center  ${
                        format.isPrimary ? "opacity-100" : "opacity-0 group-hover:opacity-100 cursor-pointer"
                      } transition-opacity z-10 text-[#FF5C35]`}
                      onClick={() => {
                        handlePrimaryTemplate(
                          {
                            isPrimary: format.isPrimary,
                            formatId: format.id,
                            orgId: format.orgId,
                          },
                          fetchTemplates,
                          fetchTemplatesRecentlyUsed
                        );
                      }}
                    >
                      <StarIcon isFill={format.isPrimary} />
                    </button>
                    {format.frontCoverImage ? (
                      <img
                        src={getImageSrc(format.frontCoverImage)}
                        className="w-full h-full object-cover"
                        draggable="false"
                        alt={`${format.formatName.replace("-", " - ")} Front Cover`}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">{index}</div>
                    )}
                  </div>
                  <h3 className="text-sm font-medium text-gray-700 text-center truncate w-full">
                    {format.formatName.replace("-", " - ")}
                  </h3>
                </div>
              </SwiperSlide>
            ))}
            </Swiper>
          </div>
        ) : (
          <div className="w-full h-26 flex justify-center">
            <Empty description="No format templates available." />
          </div>
        )}
      </div>

      <div className="flex items-center mt-4 space-x-2 text-[#FF5C35]">
        <StarIcon isFill={true} />
        <span className="text-sm font-medium text-gray-700">Primary Template</span>
      </div>

      <div className="flex flex-col mt-6">
        <Title level={3} className={"font-bold"}>
          Available in Your Organization
        </Title>
        <input
          className="w-1/2 border my-2 px-2 py-0.5 rounded border-black"
          type="text"
          placeholder="Search By Company  Name"
          value={searchQuery}
          onChange={handleSearchChange}
        />

        {formatTemplates && formatTemplates.length > 0 ? (
          <div
            ref={listRef}
            onScroll={() => {
              handleScroll();
            }}
            className=" mt-3 overflow-y-auto h-[270px] grid grid-cols-4 gap-y-3"
          >
            {formatTemplates?.map((format, num) => (
              <div key={num} className="flex flex-col items-center gap-1 w-fit">
                <div className="relative aspect-[205/111]  w-45 h-26 border-2 border-black flex items-center justify-center text-white group">
                  <button
                    type="button"
                    className={`absolute top-1 right-1 flex items-center  ${
                      format.isPrimary ? "opacity-100" : "opacity-0 group-hover:opacity-100 cursor-pointer "
                    } transition-opacity z-10 text-[#FF5C35]`}
                    onClick={() => {
                      handlePrimaryTemplate(
                        {
                          isPrimary: format.isPrimary,
                          formatId: format.id,
                          orgId: format.orgId,
                        },
                        fetchTemplates,
                        fetchTemplatesRecentlyUsed
                      );
                    }}
                  >
                    <StarIcon isFill={format.isPrimary} />
                  </button>
                  <button
                    className=" absolute bottom-1 flex items-center opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity z-10 text-[#FF5C35]
                      !bg-gray-800 !text-white hover:!bg-gray-700 px-4 py-1 rounded-md font-medium text-sm"
                    onClick={() => {
                      const { setFormatId, setFormatTemplateSidebar } = useFormatTemplateStore.getState();
                      setFormatTemplateSidebar("update");
                      setFormatId(format.id);
                    }}
                  >
                    View & Edit Template
                  </button>
                  {format.frontCoverImage ? (
                    <img
                      src={getImageSrc(format.frontCoverImage)}
                      className="w-full h-full object-contain"
                      draggable="false"
                      alt={`${format.formatName.replace("-", " - ")} Front Cover`}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">{num}</div>
                  )}
                </div>
                <h3 className="text-sm font-medium text-gray-700"> {format.formatName.replace("-", " - ")}</h3>
              </div>
            ))}
          </div>
        ) : (
          <div className="w-full h-[270px] flex justify-center py-10">
            <Empty description="No format templates available." />
          </div>
        )}
      </div>
    </div>
  );
}
